'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { Button } from './Button';
import { CandidateCardAvatar } from './CandidateCardAvatar';
import styles from './CandidateCardSnow.module.css';

export interface CandidateCardSnowSkill {
  id: number | string;
  name: string;
  type?: string;
  logo?: string;
  emoji?: string;
}

export interface CandidateCardSnowProps extends React.HTMLAttributes<HTMLDivElement> {
  candidate: {
    id: number | string;
    name: string;
    position_title?: string;
    highlights?: string;
    avatar?: string;
    blur?: boolean;
    skills?: CandidateCardSnowSkill[];
  };
  metaItems?: string[];
  ctaLabel?: string;
  recruiterLogoUrl?: string;
  recruiterName?: string;
  className?: string;
}

const MAX_SKILL_LENGTH = 50;
const MIN_SKILL_NUMBER = 5;

const ProfileIcon = () => (
  <svg
    width="14"
    height="16"
    viewBox="0 0 14 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 8a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0 1.5c-3.59 0-7 1.79-7 4v1c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-1c0-2.21-3.41-4-7-4Z"
      fill="currentColor"
    />
  </svg>
);

export const CandidateCardSnow = React.forwardRef<
  HTMLDivElement,
  CandidateCardSnowProps
>(
  (
    {
      candidate,
      metaItems,
      ctaLabel = 'View candidate profile',
      recruiterLogoUrl,
      recruiterName,
      className,
      ...props
    },
    ref
  ) => {
    const skills: CandidateCardSnowSkill[] = [];
    let characterCount = 0;
    for (const skill of candidate.skills || []) {
      if (
        characterCount + skill.name.length > MAX_SKILL_LENGTH &&
        skills.length >= MIN_SKILL_NUMBER
      ) {
        break;
      }
      skills.push(skill);
      characterCount += skill.name.length;
    }
    const additionalSkills = (candidate.skills?.length ?? 0) - skills.length;

    return (
      <div
        ref={ref}
        id={`card-${candidate.id}`}
        className={clsx(styles.root, className)}
        {...props}
      >
        <div className={styles.card}>
          <div className={styles.top}>
            <div className={styles.headings}>
              <h2 className={styles.name}>{candidate.name}</h2>
              {candidate.position_title && (
                <p className={styles.title}>{candidate.position_title}</p>
              )}
              {metaItems && metaItems.length > 0 && (
                <p className={styles.meta}>
                  {metaItems.map((item, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <span className={styles.metaSeparator} aria-hidden="true">
                          |
                        </span>
                      )}
                      {item}
                    </React.Fragment>
                  ))}
                </p>
              )}
              {candidate.highlights && (
                <p className={styles.highlights}>{candidate.highlights}</p>
              )}
            </div>

            <div className={styles.recruiterLogo}>
              {recruiterLogoUrl ? (
                <img src={recruiterLogoUrl} alt={recruiterName || ''} />
              ) : (
                <div className={styles.recruiterLogoPlaceholder} aria-hidden="true">
                  Logo
                </div>
              )}
            </div>
          </div>

          <Button
            variant="primary"
            size="lg"
            className={styles.cta}
            endIcon={
              <span className={styles.ctaIcon}>
                <ProfileIcon />
              </span>
            }
            tabIndex={-1}
            aria-hidden="true"
          >
            {ctaLabel}
          </Button>

          <div className={styles.bottom}>
            <div className={styles.avatarPanel}>
              <div className={styles.avatarWrapper}>
                <CandidateCardAvatar
                  id={`card-avatar-${candidate.id}`}
                  name={candidate.name}
                  src={candidate.avatar}
                  blur={candidate.blur}
                  size={120}
                  className={styles.avatarImage}
                  blurClassName={styles.avatarBlurred}
                />
              </div>
            </div>

            <div className={styles.skillsPanel}>
              <p className={styles.skillsLabel}>Top skills:</p>
              <div className={styles.skillsList}>
                {skills.map((item) => (
                  <span className={styles.skill} key={`${item.type}-${item.id}`}>
                    {item.logo && (
                      <img
                        src={item.logo}
                        alt={item.name}
                        title={item.name}
                        className={styles.skillLogo}
                      />
                    )}
                    {item.emoji && <span className={styles.skillEmoji}>{item.emoji}</span>}
                    {item.name}
                  </span>
                ))}
                {additionalSkills > 0 && (
                  <span className={styles.moreSkills}>+ {additionalSkills} more</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CandidateCardSnow.displayName = 'CandidateCardSnow';

export default CandidateCardSnow;
